import React, { useState, useEffect } from "react";
import CommonModal from "@/components/common/CommonModal";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { showToastNotification } from "@/utility/helper";
import Label from "@/components/ui/Label";

interface IForm {
  name: string;
  description: string;
  status: "active" | "inactive";
}

interface Props {
  show: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editData?: any; 
}

const AddDepartmentModal: React.FC<Props> = ({ show, onClose, onSuccess, editData }) => {
  const isEditMode = !!editData;

  const [form, setForm] = useState<IForm>({
    name: "",
    description: "",
    status: "active",
  });
  const [errors, setErrors] = useState<Partial<Record<keyof IForm, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (editData) {
      setForm({
        name: editData.name || "",
        description: editData.description || "",
        status: editData.status || "active",
      });
    } else {
      setForm({
        name: "",
        description: "",
        status: "active",
      });
    }
    setErrors({});
  }, [editData, show]);

  const handleChange = (field: keyof IForm, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof IForm, string>> = {};
    if (!form.name.trim()) newErrors.name = "Department name is required";
    if (!form.description.trim()) newErrors.description = "Description is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      // TODO: Replace with actual API call
      // if (isEditMode) { await DepartmentService.update(...) } else { await DepartmentService.create(...) }
      await new Promise((resolve) => setTimeout(resolve, 1000));
      showToastNotification("success", isEditMode ? "Department updated" : "Department created");
      onSuccess();
      onClose();
    } catch (error) {
      showToastNotification("danger", "Something went wrong");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <CommonModal
      title={isEditMode ? "Edit Department" : "Add Department"}
      show={show}
      onClose={onClose}
      className="max-w-lg"
    >
      <form onSubmit={handleSubmit} className="space-y-5 pt-2">
        <div className="space-y-1.5">
          <Label htmlFor="deptName">
            Department Name <span className="text-red-500">*</span>
          </Label>
          <Input
            id="deptName"
            placeholder="e.g. Civil Engineering"
            value={form.name}
            onChange={(e) => handleChange("name", e.target.value)}
            className={errors.name ? "border-red-500 focus-visible:ring-red-500" : ""}
          />
          {errors.name && <p className="text-xs text-red-500">{errors.name}</p>}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="description">
            Description <span className="text-red-500">*</span>
          </Label>
          <Input
            id="description"
            placeholder="Brief description of the department"
            value={form.description}
            onChange={(e) => handleChange("description", e.target.value)}
            className={errors.description ? "border-red-500 focus-visible:ring-red-500" : ""}
          />
          {errors.description && <p className="text-xs text-red-500">{errors.description}</p>}
        </div>

        <div className="space-y-1.5">
          <Label>Status</Label>
          <div className="flex gap-2">
            {(["active", "inactive"] as const).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => handleChange("status", s)}
                className={`px-4 py-1.5 rounded-full text-xs font-medium border transition ${
                  form.status === s
                    ? s === "active"
                      ? "bg-green-50 text-green-600 border-green-600"
                      : "bg-red-50 text-red-600 border-red-600"
                    : "bg-white text-gray-500 border-gray-200 hover:bg-gray-50"
                }`}
              >
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 pt-2 border-t border-slate-100">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="border-slate-200 text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Saving..." : isEditMode ? "Update Department" : "Save Department"}
          </Button>
        </div>
      </form>
    </CommonModal>
  );
};

export default AddDepartmentModal;